import { Static, Type } from "@sinclair/typebox";
import { ThreadMessagesResponseType } from "./ThreadMessagesResponse";
import { LeadStatusType } from "@whatslly/types/LeadStatus";
import { LayoutField } from "@whatslly/types/LayoutFieldType";

export interface SfUserPermissionQueryApiResponse {
  success: boolean;
  message?: string;
}

export type SfUserProfileResponse =
  | { success: false; message: string }
  | {
      userInfo: {
        roleName: string | null;
        roleId: string | null;
        profileName: string | null;
        profileId: string | null;
        managerId: string | null;
        managerName: string | null;
      };
      success: true;
    };

export interface SfQueryApiResponse {
  totalRecords: number;
  success: boolean;
  searchRecords: SearchRecordType[];
  oppsOrder?: OppsOrderType[];
  opps?: OppType[];
  notifications: number;
  msgs?: ThreadMessagesResponseType;
  message: string;
  matchedByPhone: boolean;
  matchedByName: boolean;
  leadStatuses?: LeadStatusType[];
  leadStatus?: string;
  additionalObjectsRecords?: Array<Record<string, string>[]>;
  threadsResponse:
    | null
    | { success: false; message?: string }
    | { success: true; message?: string; people?: number; received?: number; saved?: number };
}

export interface SfWhatsllyUploadApiResponse {
  success: boolean;
  inserted: number;
  contentVersions: {
    ContentLocation: string;
    PathOnClient: string;
    Origin: string;
    OwnerId: string;
    Title: string;
    FirstPublishLocationId: string;
    Id: string;
  }[];
}

const TvsAction = Type.Object({
  action: Type.Union([Type.Literal("set"), Type.Literal("modal")]),
  fields: Type.Optional(Type.Array(LayoutField)),
});

export type TvsActionType = Static<typeof TvsAction>;

export const OppsOrder = Type.Object(
  {
    MasterLabel: Type.String(),
    ApiName: Type.String(),
    IsWon: Type.Optional(Type.Boolean()),
    IsClosed: Type.Boolean(),
    SortOrder: Type.Number(),
    Id: Type.String(),
    SubOrders: Type.Optional(Type.Any()),
    tvsAction: Type.Optional(TvsAction),
  },
  // Salesforce returns Opportunity stages with additional metadata fields not modeled here
  // (forecast categories, descriptions). Open to keep them on the wire.
  { additionalProperties: true }
);

export type OppsOrderType = Static<typeof OppsOrder>;

export const Opp = Type.Object(
  {
    Id: Type.String(),
    Name: Type.String(),
    StageName: Type.String(),
    Amount: Type.Optional(Type.Number()),
    IsClosed: Type.Boolean(),
    NextStep: Type.Optional(Type.String()),
    CloseDate: Type.Optional(Type.String()),
    CreatedDate: Type.Optional(Type.String()),
    RecordTypeId: Type.Optional(Type.String()),
    OpStages: Type.Optional(Type.Array(OppsOrder)),
  },
  // Opportunities carry org-specific custom fields (`*__c`); open the wire so they survive
  // fast-json-stringify serialization for the widget's opportunity panels.
  { additionalProperties: true }
);

export type OppType = Static<typeof Opp>;

export enum AttributeEnum {
  Account = "Account",
  Contact = "Contact",
  Opportunity = "Opportunity",
  OpportunityStage = "OpportunityStage",
  Lead = "Lead",
  LeadStatus = "LeadStatus",
}

const Attributes = Type.Object({
  type: Type.Enum(AttributeEnum),
  url: Type.String(),
});

export type AttributesType = Static<typeof Attributes>;

export const Account = Type.Record(Type.String(), Type.Union([Type.Number(), Type.String(), Attributes]));

const Address = Type.Object({
  city: Type.String(),
  country: Type.String(),
  geocodeAccuracy: Type.Optional(Type.Number()),
  latitude: Type.Optional(Type.Number()),
  longitude: Type.Optional(Type.Number()),
  postalCode: Type.String(),
  state: Type.String(),
  street: Type.String(),
});

export const SearchRecord = Type.Object({
  IsPersonAccount: Type.Boolean(),
  attributes: Attributes,
  Phone: Type.Optional(Type.String()),
  MobilePhone: Type.Optional(Type.String()),
  PersonPhone: Type.Optional(Type.String()),
  MainPhone: Type.Optional(Type.String()),
  Name: Type.String(),
  Id: Type.String(),
  Account: Type.Optional(Account),
  Address: Type.Optional(Address),
  whatslly__Whatslly_Person_Id__c: Type.Optional(Type.String()),
  isDefaultRecord: Type.Optional(Type.Boolean()),
  RecordTypeId: Type.Optional(Type.String()),
  whatslly__Tuvis_Chat_Id__c: Type.Optional(Type.String()),
});

export type SearchRecordType = Static<typeof SearchRecord>;
